import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'

import { PageLayout } from '@components/layouts/PageLayout'
import { Button, Link, Alert } from '@atoms'
import Badge from '@components/atoms/Badge'
import { SelectField } from '@components/molecules/SelectField'
import Steps from '@components/molecules/Steps'
import { MultiSelectField } from '@components/molecules/MultiSelectField'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/atoms/Tooltip'

/**
 * Import events and rosters (design-phase prototype)
 *
 * Reworks `/imports/events` from "one button that opens CSVBox" into three
 * steps: build a template configured for this instance, upload it through
 * CSVBox, then confirm what was submitted.
 *
 * Sample data only — no controller, GraphQL or CSVBox behind it. Any step can
 * be opened directly with `?step=template|upload|confirm` (or `?step=3`), which
 * is how to put the confirm screen in front of someone without walking the
 * flow. The "Design tools" overlay at the bottom-left is dev-only and not part
 * of the product.
 */

// ---------------------------------------------------------------------------
// Sample instance data (would be props from EventImportsController)
// ---------------------------------------------------------------------------

/** Sized to match a large instance, so the picker is exercised honestly. */
const GROUPS = [
  'Adelaide',
  'Aston',
  'Ballarat',
  'Banks',
  'Barton',
  'Bendigo',
  'Bennelong',
  'Berowra',
  'Blaxland',
  'Bonner',
  'Boothby',
  'Bradfield',
  'Brisbane',
  'Bruce',
  'Calare',
  'Canberra',
  'Casey',
  'Chisholm',
  'Cooper',
  'Corangamite',
  'Cowper',
  'Curtin',
  'Deakin',
  'Dickson',
  'Dunkley',
  'Eden-Monaro',
  'Fisher',
  'Flinders',
  'Fraser',
  'Fremantle',
  'Gilmore',
  'Goldstein',
  'Grayndler',
  'Griffith',
  'Hasluck',
  'Higgins',
  'Hughes',
  'Hume',
  'Indi',
  'Isaacs',
  'Kooyong',
  'La Trobe',
  'Lilley',
  'Lindsay',
  'Lyons',
  'Macnamara',
  'Mackellar',
  'Macquarie',
  'McEwen',
  'Melbourne',
  'Menzies',
  'Monash',
  'Moore',
  'North Sydney',
  'Parramatta',
  'Pearce',
  'Reid',
  'Ryan',
  'Sturt',
  'Swan',
  'Tangney',
  'Wannon',
  'Warringah',
  'Wentworth',
  'Wills',
].map((name) => `${name} Volunteers`)

const EVENT_TYPES = ['Doorknock', 'Phone bank', 'Street stall', 'Volunteer training']

/** Matches `Event#status`. The file carries these values verbatim. */
const STATUS_OPTIONS = [
  { value: 'published', label: 'Published' },
  { value: 'unlisted', label: 'Unlisted' },
]

/**
 * `ActiveSupport::TimeZone` names, which is what the importer already writes to
 * `Event#time_zone` (bare city names, not IANA "Australia/Sydney"). The real
 * prop is all 152 zones; these are the Australian ones plus a few others so the
 * picker behaves like the real list. Adelaide, Darwin and Hobart matter: they
 * are half-hour offsets or have their own daylight saving dates.
 */
const TIME_ZONES = [
  'Sydney',
  'Melbourne',
  'Brisbane',
  'Perth',
  'Adelaide',
  'Darwin',
  'Hobart',
  'Canberra',
  'Auckland',
  'London',
  'UTC',
]

/** `ActiveSupport::TimeZone.all.size`. Every zone is offered, unfiltered. */
const TOTAL_TIME_ZONES = 152

const AUTORESPONSE_OPTIONS = [
  { value: 'true', label: 'On' },
  { value: 'false', label: 'Off' },
]

/** Proposed CSVBox `max_rows` — currently unset, which means no limit at all. */
const MAX_EVENT_ROWS = 500

/**
 * Stands in for the `maxShiftsCount` prop, which EventImportsController reads
 * from `CSV_BOX_MAX_SHIFTS_COUNT` (default 10). It is the hard ceiling on shift
 * column groups for this instance, so the field cannot go above it.
 */
const MAX_SHIFTS_COUNT = 10

// ---------------------------------------------------------------------------
// Template model
// ---------------------------------------------------------------------------

type FileFormat = 'xlsx' | 'csv'

type ColumnKind = 'list' | 'date' | 'time' | 'number' | 'text'

type ColumnSpec = { name: string; kind: ColumnKind }

type OptionalBlockKey = 'location' | 'geo' | 'contact' | 'content'

type OptionalBlock = {
  key: OptionalBlockKey
  label: string
  hint: string
  columns: ColumnSpec[]
}

const OPTIONAL_BLOCKS: OptionalBlock[] = [
  {
    key: 'location',
    label: 'Venue and address',
    hint: 'Where the event happens. Leave off for online events.',
    columns: [
      { name: 'venue_name', kind: 'text' },
      { name: 'street_address_1', kind: 'text' },
      { name: 'suburb', kind: 'text' },
      { name: 'state', kind: 'text' },
      { name: 'postcode', kind: 'text' },
    ],
  },
  {
    key: 'geo',
    label: 'Map coordinates',
    hint: 'Only needed when an address does not map correctly.',
    columns: [
      { name: 'latitude', kind: 'number' },
      { name: 'longitude', kind: 'number' },
    ],
  },
  {
    key: 'contact',
    label: 'Event contact',
    hint: 'Shown to supporters on the event page.',
    columns: [
      { name: 'contact_name', kind: 'text' },
      { name: 'contact_email', kind: 'text' },
      { name: 'contact_phone', kind: 'text' },
    ],
  },
  {
    key: 'content',
    label: 'Description and tags',
    hint: 'Page content and comma separated tags.',
    columns: [
      { name: 'content', kind: 'text' },
      { name: 'tags', kind: 'text' },
    ],
  },
]

const SHIFT_COLUMN_KINDS: Array<{ suffix: string; kind: ColumnKind }> = [
  { suffix: 'role_name', kind: 'text' },
  { suffix: 'date', kind: 'date' },
  { suffix: 'start_time', kind: 'time' },
  { suffix: 'end_time', kind: 'time' },
  { suffix: 'number_of_volunteers', kind: 'number' },
]

/**
 * Each of these is either one value applied to every event, or a column in the
 * file. `value` is only meaningful when the field is fixed.
 */
type EventSettingKey = 'eventType' | 'status' | 'timeZone' | 'autoresponse'

type EventSetting = { perEvent: boolean; value: string }

type EventSettings = Record<EventSettingKey, EventSetting>

type TemplateState = {
  fileName: string
  groups: string[]
  shiftCount: number
  format: FileFormat
  optionalBlocks: Record<OptionalBlockKey, boolean>
  settings: EventSettings
  exampleRows: 0 | 1 | 3
}

const INITIAL_TEMPLATE: TemplateState = {
  fileName: 'events-import',
  groups: GROUPS,
  shiftCount: 4,
  format: 'xlsx',
  optionalBlocks: { location: true, geo: false, contact: true, content: false },
  settings: {
    eventType: { perEvent: true, value: EVENT_TYPES[0] },
    status: { perEvent: true, value: 'published' },
    timeZone: { perEvent: false, value: 'Sydney' },
    autoresponse: { perEvent: false, value: 'true' },
  },
  exampleRows: 3,
}

const SETTING_COLUMNS: Record<EventSettingKey, ColumnSpec> = {
  eventType: { name: 'event_type', kind: 'list' },
  status: { name: 'status', kind: 'list' },
  timeZone: { name: 'time_zone', kind: 'list' },
  autoresponse: { name: 'enable_autoresponse', kind: 'list' },
}

function buildColumns(template: TemplateState): ColumnSpec[] {
  const columns: ColumnSpec[] = [
    { name: 'group_name', kind: 'list' },
    { name: 'event_name', kind: 'text' },
  ]

  if (template.settings.status.perEvent) columns.push(SETTING_COLUMNS.status)
  if (template.settings.eventType.perEvent) columns.push(SETTING_COLUMNS.eventType)

  columns.push(
    { name: 'start_date', kind: 'date' },
    { name: 'start_time', kind: 'time' },
    { name: 'end_date', kind: 'date' },
    { name: 'end_time', kind: 'time' }
  )

  if (template.settings.timeZone.perEvent) columns.push(SETTING_COLUMNS.timeZone)
  if (template.settings.autoresponse.perEvent) columns.push(SETTING_COLUMNS.autoresponse)

  OPTIONAL_BLOCKS.forEach((block) => {
    if (template.optionalBlocks[block.key]) columns.push(...block.columns)
  })

  for (let i = 1; i <= template.shiftCount; i += 1) {
    SHIFT_COLUMN_KINDS.forEach(({ suffix, kind }) => columns.push({ name: `shift_${i}_${suffix}`, kind }))
  }

  return columns
}

const SAMPLE_VALUES: Record<string, string[]> = {
  group_name: ['Warringah Volunteers', 'Mackellar Volunteers', 'Warringah Volunteers'],
  event_name: ['EXAMPLE EVENT 1', 'EXAMPLE EVENT 2', 'EXAMPLE EVENT 3'],
  status: ['published', 'published', 'unlisted'],
  event_type: ['Doorknock', 'Phone bank', 'Street stall'],
  start_date: ['2026-09-12', '2026-09-15', '2026-09-19'],
  start_time: ['9:00AM', '5:30PM', '8:00AM'],
  end_date: ['2026-09-12', '2026-09-15', '2026-09-19'],
  end_time: ['12:00PM', '8:30PM', '1:00PM'],
  time_zone: ['Sydney', 'Sydney', 'Darwin'],
  venue_name: ['Dee Why Library', 'Campaign office', 'Manly Corso'],
  street_address_1: ['725 Pittwater Rd', '12 Sydney Rd', 'The Corso'],
  suburb: ['Dee Why', 'Manly', 'Manly'],
  state: ['NSW', 'NSW', 'NSW'],
  postcode: ['2099', '2095', '2095'],
  latitude: ['-33.7515', '-33.7969', '-33.7969'],
  longitude: ['151.2870', '151.2854', '151.2854'],
  contact_name: ['Priya Raman', 'Priya Raman', 'Tom Alder'],
  contact_email: ['priya@example.org', 'priya@example.org', 'tom@example.org'],
  contact_phone: ['0412 000 111', '0412 000 111', '0413 222 333'],
  content: ['Meet at the library steps.', 'Scripts provided.', 'Gazebo supplied.'],
  tags: ['doorknock, north', 'phones', 'stall, manly'],
  enable_autoresponse: ['On', 'On', 'Off'],
}

function sampleFor(column: string, rowIndex: number): string {
  const shiftMatch = column.match(/^shift_(\d+)_(.+)$/)

  if (shiftMatch) {
    const shiftIndex = Number(shiftMatch[1])
    // Only the first two shifts are filled, so the pattern is obvious without
    // implying every shift column must be populated.
    if (shiftIndex > 2) return ''

    const startHour = 9 + (shiftIndex - 1) * 3
    switch (shiftMatch[2]) {
      case 'role_name':
        return shiftIndex === 1 ? 'Doorknocker' : 'Team leader'
      case 'date':
        return SAMPLE_VALUES.start_date[rowIndex]
      case 'start_time':
        return `${startHour}:00AM`
      case 'end_time':
        return `${startHour + 3}:00PM`
      case 'number_of_volunteers':
        return shiftIndex === 1 ? '8' : '2'
      default:
        return ''
    }
  }

  return SAMPLE_VALUES[column]?.[rowIndex] ?? ''
}

const KIND_LABELS: Record<ColumnKind, string> = {
  list: 'Dropdown',
  date: 'Date',
  time: 'Time',
  number: 'Number',
  text: 'Text',
}

const KIND_LEGEND: Array<{ kind: ColumnKind; color: 'primary' | 'info' | 'muted' | 'gray'; note: string }> = [
  { kind: 'list', color: 'primary', note: 'pick from a list' },
  { kind: 'date', color: 'info', note: 'set to YYYY-MM-DD' },
  { kind: 'time', color: 'muted', note: 'set to text, so 9:00AM stays 9:00AM' },
  { kind: 'number', color: 'gray', note: 'whole numbers' },
  { kind: 'text', color: 'gray', note: 'anything you type' },
]

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={clsx('tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs', className)}>
      {children}
    </div>
  )
}

function CardHeading({ icon, title, subtitle }: { icon: IconTypes; title: string; subtitle?: string }) {
  return (
    <div className="tw:flex tw:items-start tw:gap-3 tw:border-b tw:border-gray-200 tw:px-5 tw:py-4">
      <span className="tw:mt-0.5 tw:flex tw:h-8 tw:w-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-lg tw:bg-primary-50 tw:text-primary-600">
        <Icon type={icon} className="tw:h-4 tw:w-4" />
      </span>
      <div>
        <h3 className="tw:text-base tw:font-bold tw:text-gray-cool-900">{title}</h3>
        {subtitle && <p className="tw:mt-0.5 tw:text-sm tw:text-gray-cool-600">{subtitle}</p>}
      </div>
    </div>
  )
}

/** Alert children do not inherit a size, so every body needs `tw:text-sm`. */
function AlertText({ children }: { children: React.ReactNode }) {
  return <span className="tw:text-sm">{children}</span>
}

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="tw:flex tw:items-center tw:gap-1.5">
      <span className="tw:text-sm tw:font-medium tw:text-gray-cool-900">{children}</span>
      {hint && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="tw:inline-flex tw:text-gray-cool-400" aria-label={hint} tabIndex={0}>
                <Icon type="help-circle" className="tw:h-4 tw:w-4" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{hint}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  )
}

function ToggleRow({
  checked,
  onChange,
  label,
  hint,
  meta,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  hint: string
  meta?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={clsx(
        'tw:flex tw:w-full tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:p-3 tw:text-left tw:transition',
        checked
          ? 'tw:border-primary-300 tw:bg-primary-50/60'
          : 'tw:border-gray-200 tw:bg-white tw:hover:border-gray-cool-300'
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'tw:mt-0.5 tw:flex tw:h-5 tw:w-5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded tw:border',
          checked ? 'tw:border-primary-600 tw:bg-primary-600 tw:text-white' : 'tw:border-gray-cool-300 tw:bg-white'
        )}
      >
        {checked && <Icon type="check" className="tw:h-3.5 tw:w-3.5" />}
      </span>
      <span className="tw:flex tw:flex-col tw:gap-0.5">
        <span className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium tw:text-gray-cool-900">
          {label}
          {meta && (
            <Badge color="muted" shape="rounded">
              {meta}
            </Badge>
          )}
        </span>
        <span className="tw:text-xs tw:text-gray-cool-600">{hint}</span>
      </span>
    </button>
  )
}

function Chip({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={clsx(
        'tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-full tw:border tw:px-3 tw:py-1.5 tw:text-sm tw:transition',
        selected
          ? 'tw:border-primary-300 tw:bg-primary-50 tw:font-medium tw:text-primary-700'
          : 'tw:border-gray-cool-200 tw:bg-white tw:text-gray-cool-600 tw:hover:border-gray-cool-400'
      )}
    >
      {selected && <Icon type="check" className="tw:h-3.5 tw:w-3.5" />}
      {label}
    </button>
  )
}

function SegControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      <span className="tw:text-xs tw:font-bold tw:uppercase tw:tracking-wide tw:text-gray-400">{label}</span>
      <div className="tw:inline-flex tw:w-fit tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-300">
        {options.map((o, i) => (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            aria-pressed={value === o.value}
            className={clsx(
              'tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium',
              i > 0 && 'tw:border-l tw:border-gray-300',
              value === o.value
                ? 'tw:bg-primary-50 tw:text-primary-700'
                : 'tw:bg-white tw:text-gray-600 tw:hover:bg-gray-50'
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 1 — build the template
// ---------------------------------------------------------------------------

const STEP_LABELS = ['Build template', 'Upload file', 'Confirm']

function GroupPicker({
  template,
  setTemplate,
}: {
  template: TemplateState
  setTemplate: React.Dispatch<React.SetStateAction<TemplateState>>
}) {
  const allSelected = template.groups.length === GROUPS.length

  return (
    <Card>
      <CardHeading
        icon="users-01"
        title="Groups in this file"
        subtitle="The group column becomes a dropdown of the groups you choose."
      />
      <div className="tw:flex tw:flex-col tw:gap-3 tw:p-5">
        <div className="tw:min-w-[18rem] tw:max-w-md">
          <MultiSelectField
            label="Groups"
            placeholder="Choose groups"
            showSelectAll
            selectedLabel={(selected) =>
              selected.length === 0
                ? undefined
                : allSelected
                  ? `All ${GROUPS.length} groups`
                  : `${selected.length} of ${GROUPS.length} groups`
            }
            options={GROUPS.map((name) => ({ value: name, label: name }))}
            value={template.groups}
            onValueChange={(next) => setTemplate((prev) => ({ ...prev, groups: next }))}
            searchPlaceholder="Search groups"
            noResultsText="No groups match that search"
          />
        </div>

        <p className="tw:text-xs tw:text-gray-cool-600">
          Every group you can create events in is selected. Remove the ones you do not need.
        </p>

        {template.groups.length > 0 && !allSelected && (
          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {template.groups.slice(0, 8).map((group) => (
              <Badge key={group} color="primary" shape="rounded" bordered>
                {group}
              </Badge>
            ))}
            {template.groups.length > 8 && (
              <Badge color="muted" shape="rounded">
                +{template.groups.length - 8} more
              </Badge>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

const clampShifts = (value: number) => Math.min(MAX_SHIFTS_COUNT, Math.max(0, value))

function ShiftCountField({
  template,
  setTemplate,
}: {
  template: TemplateState
  setTemplate: React.Dispatch<React.SetStateAction<TemplateState>>
}) {
  const [draft, setDraft] = useState(String(template.shiftCount))

  const commit = (raw: string) => {
    const parsed = Number.parseInt(raw, 10)
    const next = Number.isNaN(parsed) ? template.shiftCount : clampShifts(parsed)
    setDraft(String(next))
    setTemplate((prev) => ({ ...prev, shiftCount: next }))
  }

  const step = (delta: number) => {
    const next = clampShifts(template.shiftCount + delta)
    setDraft(String(next))
    setTemplate((prev) => ({ ...prev, shiftCount: next }))
  }

  return (
    <Card>
      <CardHeading
        icon="clock"
        title="Shifts per event"
        subtitle="Use the highest number any one event needs. Set it to 0 for events with no shifts."
      />
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
          <div className="tw:inline-flex tw:items-center tw:overflow-hidden tw:rounded-lg tw:border tw:border-gray-cool-300">
            <button
              type="button"
              aria-label="One less shift"
              onClick={() => step(-1)}
              className="tw:px-3 tw:py-2 tw:text-gray-cool-600 tw:hover:bg-gray-50"
            >
              <Icon type="minus-circle" className="tw:h-4 tw:w-4" />
            </button>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={MAX_SHIFTS_COUNT}
              aria-label="Shifts per event"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={(event) => commit(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commit((event.target as HTMLInputElement).value)
              }}
              className="tw:w-16 tw:border-x tw:border-gray-cool-300 tw:px-2 tw:py-2 tw:text-center tw:text-base tw:font-bold tw:text-gray-cool-900 tw:focus:outline-hidden tw:focus:ring-2 tw:focus:ring-primary-300"
            />
            <button
              type="button"
              aria-label="One more shift"
              onClick={() => step(1)}
              className="tw:px-3 tw:py-2 tw:text-gray-cool-600 tw:hover:bg-gray-50"
            >
              <Icon type="plus-circle" className="tw:h-4 tw:w-4" />
            </button>
          </div>
          <span className="tw:text-sm tw:text-gray-cool-600">
            {template.shiftCount === 0 ? 'no shift columns' : `shifts per event, up to ${MAX_SHIFTS_COUNT}`}
          </span>
        </div>

        <p className="tw:text-xs tw:text-gray-cool-600">
          This instance is set to a maximum of {MAX_SHIFTS_COUNT} shifts per event. Contact SupporterBase to change
          it.
        </p>

        {template.shiftCount === 0 && (
          <Alert type="info" size="sm">
            <AlertText>
              Events only. The file has no shift columns, so no rosters are created. Add shifts to an event after it
              is imported.
            </AlertText>
          </Alert>
        )}

        {template.shiftCount === MAX_SHIFTS_COUNT && MAX_SHIFTS_COUNT > 6 && (
          <Alert type="warning" size="sm">
            <AlertText>
              At {MAX_SHIFTS_COUNT} shifts the file gets wide to scroll. Build a second template for your busiest days.
            </AlertText>
          </Alert>
        )}
      </div>
    </Card>
  )
}

/**
 * One control per setting instead of a mode switch plus a value picker. The
 * first option means "this varies, give me a column"; every other option fixes
 * the value for every event in the file.
 */
const PER_EVENT = '__per_event__'

function EventSettingsCard({
  template,
  setTemplate,
}: {
  template: TemplateState
  setTemplate: React.Dispatch<React.SetStateAction<TemplateState>>
}) {
  const update = (key: EventSettingKey, patch: Partial<EventSetting>) =>
    setTemplate((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: { ...prev.settings[key], ...patch } },
    }))

  return (
    <Card>
      <CardHeading
        icon="settings-01"
        title="Event settings"
        subtitle="Set one value for every event, or add a column so each event can differ."
      />
      <div className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5">
        <SettingRow
          label="Event type"
          columnName="event_type"
          setting={template.settings.eventType}
          options={EVENT_TYPES.map((name) => ({ value: name, label: name }))}
          onChange={(patch) => update('eventType', patch)}
        />
        <SettingRow
          label="Status"
          hint="Published events appear on the website. Unlisted events are only reachable by link."
          columnName="status"
          setting={template.settings.status}
          options={STATUS_OPTIONS}
          onChange={(patch) => update('status', patch)}
        />
        <SettingRow
          label="Time zone"
          hint={`All ${TOTAL_TIME_ZONES} time zones are offered, including Adelaide, Darwin and Hobart.`}
          columnName="time_zone"
          setting={template.settings.timeZone}
          options={TIME_ZONES.map((name) => ({ value: name, label: name }))}
          onChange={(patch) => update('timeZone', patch)}
        />
        <SettingRow
          label="RSVP autoresponse"
          hint="Uses the subject and body set on the event type."
          columnName="enable_autoresponse"
          setting={template.settings.autoresponse}
          options={AUTORESPONSE_OPTIONS}
          onChange={(patch) => update('autoresponse', patch)}
        />
      </div>
    </Card>
  )
}

function SettingRow({
  label,
  hint,
  columnName,
  setting,
  options,
  onChange,
}: {
  label: string
  hint?: string
  columnName: string
  setting: EventSetting
  options: Array<{ value: string; label: string }>
  onChange: (patch: Partial<EventSetting>) => void
}) {
  const selectOptions = [{ value: PER_EVENT, label: 'Different for each event' }, ...options]
  const selectedLabel = options.find((option) => option.value === setting.value)?.label ?? setting.value

  return (
    <div className="tw:flex tw:flex-col tw:gap-2 tw:py-4 tw:sm:flex-row tw:sm:items-center tw:sm:justify-between tw:sm:gap-6">
      <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
        <FieldLabel hint={hint}>{label}</FieldLabel>
        <span className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-gray-cool-600">
          {setting.perEvent ? (
            <>
              <Badge color="primary" shape="rounded">
                Column
              </Badge>
              <span className="tw:font-mono">{columnName}</span>
            </>
          ) : (
            <>
              <Badge color="muted" shape="rounded">
                No column
              </Badge>
              <span>every event is {selectedLabel}</span>
            </>
          )}
        </span>
      </div>

      <div className="tw:w-full tw:sm:w-64 tw:sm:shrink-0">
        <SelectField
          hideLabel
          name={columnName}
          label={label}
          options={selectOptions}
          value={setting.perEvent ? PER_EVENT : setting.value}
          onValueChange={(next) => onChange(next === PER_EVENT ? { perEvent: true } : { perEvent: false, value: next })}
        />
      </div>
    </div>
  )
}

function TemplateBuilder({
  template,
  setTemplate,
}: {
  template: TemplateState
  setTemplate: React.Dispatch<React.SetStateAction<TemplateState>>
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-5">
      <GroupPicker template={template} setTemplate={setTemplate} />
      <ShiftCountField template={template} setTemplate={setTemplate} />

      <Card>
        <CardHeading
          icon="file-05"
          title="File format"
          subtitle="Both upload the same way. Only Excel files carry dropdowns."
        />
        <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
          <div className="tw:grid tw:gap-3 tw:md:grid-cols-2">
            <FormatOption
              selected={template.format === 'xlsx'}
              onClick={() => setTemplate((p) => ({ ...p, format: 'xlsx' }))}
              title="Excel (.xlsx)"
              recommended
              points={[
                'Dropdowns for group, event type, status and time zone',
                'Date and time columns set to the format the uploader expects',
                'A reference sheet listing every valid value',
              ]}
            />
            <FormatOption
              selected={template.format === 'csv'}
              onClick={() => setTemplate((p) => ({ ...p, format: 'csv' }))}
              title="Plain CSV (.csv)"
              points={[
                'Headers and example rows only',
                'No dropdowns. A CSV file cannot store them',
                'Use this if another system creates your file',
              ]}
            />
          </div>

          {template.format === 'xlsx' && (
            <Alert type="info" size="sm">
              <AlertText>
                Dropdowns work in Excel and Google Sheets. Apple Numbers removes them when it opens the file. The
                uploader checks every value either way.
              </AlertText>
            </Alert>
          )}
        </div>
      </Card>

      <Card>
        <CardHeading
          icon="columns-03"
          title="Extra columns"
          subtitle="Turn off what you do not need. You can add it to events later."
        />
        <div className="tw:grid tw:gap-3 tw:p-5 tw:md:grid-cols-2">
          {OPTIONAL_BLOCKS.map((block) => (
            <ToggleRow
              key={block.key}
              checked={template.optionalBlocks[block.key]}
              onChange={(next) =>
                setTemplate((prev) => ({
                  ...prev,
                  optionalBlocks: { ...prev.optionalBlocks, [block.key]: next },
                }))
              }
              label={block.label}
              hint={block.hint}
              meta={`${block.columns.length} cols`}
            />
          ))}
        </div>
      </Card>

      <EventSettingsCard template={template} setTemplate={setTemplate} />

      <Card>
        <CardHeading icon="clipboard-minus" title="Example rows" subtitle="Rows to copy. Delete them before you upload." />
        <div className="tw:p-5">
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            {([0, 1, 3] as const).map((count) => (
              <Chip
                key={count}
                label={count === 0 ? 'None' : `${count} row${count > 1 ? 's' : ''}`}
                selected={template.exampleRows === count}
                onClick={() => setTemplate((p) => ({ ...p, exampleRows: count }))}
              />
            ))}
          </div>
        </div>
      </Card>
    </div>
  )
}

function FormatOption({
  selected,
  onClick,
  title,
  points,
  recommended,
}: {
  selected: boolean
  onClick: () => void
  title: string
  points: string[]
  recommended?: boolean
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={clsx(
        'tw:flex tw:h-full tw:flex-col tw:gap-2 tw:rounded-lg tw:border tw:p-4 tw:text-left tw:transition',
        selected
          ? 'tw:border-primary-300 tw:bg-primary-50/60'
          : 'tw:border-gray-200 tw:bg-white tw:hover:border-gray-cool-300'
      )}
    >
      <span className="tw:flex tw:items-center tw:gap-2">
        <span
          aria-hidden
          className={clsx(
            'tw:flex tw:h-4 tw:w-4 tw:items-center tw:justify-center tw:rounded-full tw:border',
            selected ? 'tw:border-primary-600 tw:bg-primary-600 tw:text-white' : 'tw:border-gray-cool-300'
          )}
        >
          {selected && <Icon type="check" className="tw:h-3 tw:w-3" />}
        </span>
        <span className="tw:text-sm tw:font-bold tw:text-gray-cool-900">{title}</span>
        {recommended && (
          <Badge color="success" shape="rounded">
            Recommended
          </Badge>
        )}
      </span>
      <ul className="tw:flex tw:flex-col tw:gap-1 tw:pl-6 tw:text-xs tw:text-gray-cool-600">
        {points.map((point) => (
          <li key={point} className="tw:list-disc">
            {point}
          </li>
        ))}
      </ul>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Template summary + preview
// ---------------------------------------------------------------------------

function TemplateSummary({
  template,
  columns,
  setTemplate,
}: {
  template: TemplateState
  columns: ColumnSpec[]
  setTemplate: React.Dispatch<React.SetStateAction<TemplateState>>
}) {
  const setFileName = (fileName: string) => setTemplate((prev) => ({ ...prev, fileName }))

  return (
    <Card className="tw:sticky tw:top-4">
      <CardHeading icon="file-download-03" title="Your template" />
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-5">
        <div className="tw:grid tw:grid-cols-2 tw:gap-3">
          <Capacity value={MAX_EVENT_ROWS.toString()} label="events per file" />
          <Capacity
            value={template.shiftCount === 0 ? 'None' : template.shiftCount.toString()}
            label="shifts per event"
          />
        </div>

        <p className="tw:text-xs tw:text-gray-cool-600">
          {template.shiftCount === 0 ? (
            <>Events only, with no shift columns. Split files over {MAX_EVENT_ROWS} events.</>
          ) : (
            <>
              Up to {(MAX_EVENT_ROWS * template.shiftCount).toLocaleString()} shifts in one file. Split files over{' '}
              {MAX_EVENT_ROWS} events.
            </>
          )}
        </p>

        {template.groups.length === 0 && (
          <Alert type="warning" size="sm">
            <AlertText>Choose at least one group.</AlertText>
          </Alert>
        )}

        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <label htmlFor="template-file-name" className="tw:text-xs tw:font-medium tw:text-gray-cool-900">
            File name
          </label>
          <div className="tw:flex tw:items-center tw:rounded-lg tw:border tw:border-gray-cool-300 tw:px-3 tw:py-2 tw:focus-within:border-primary-500 tw:focus-within:ring-2 tw:focus-within:ring-primary-200">
            <input
              id="template-file-name"
              type="text"
              value={template.fileName}
              onChange={(event) => setFileName(event.target.value)}
              placeholder="events-import"
              className="tw:min-w-0 tw:flex-1 tw:border-0 tw:p-0 tw:text-sm tw:text-gray-cool-900 tw:focus:outline-hidden tw:focus:ring-0"
            />
            <span className="tw:shrink-0 tw:text-sm tw:text-gray-cool-400">.{template.format}</span>
          </div>
        </div>

        <dl className="tw:flex tw:flex-col tw:gap-1.5 tw:border-t tw:border-gray-100 tw:pt-3 tw:text-xs">
          <SummaryRow label="Groups in dropdown" value={template.groups.length.toString()} />
          <SummaryRow label="Columns" value={columns.length.toString()} />
          <SummaryRow label="Example rows" value={template.exampleRows === 0 ? 'None' : template.exampleRows.toString()} />
        </dl>
      </div>
    </Card>
  )
}

function Capacity({ value, label }: { value: string; label: string }) {
  return (
    <div className="tw:rounded-lg tw:bg-gray-cool-50 tw:px-3 tw:py-3">
      <p className="tw:text-2xl tw:font-bold tw:leading-none tw:text-gray-cool-900">{value}</p>
      <p className="tw:mt-1 tw:text-xs tw:text-gray-cool-600">{label}</p>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="tw:flex tw:items-baseline tw:justify-between tw:gap-4">
      <dt className="tw:text-gray-cool-600">{label}</dt>
      <dd className="tw:font-medium tw:text-gray-cool-900">{value}</dd>
    </div>
  )
}

function FilePreview({ template, columns }: { template: TemplateState; columns: ColumnSpec[] }) {
  const rowCount = template.exampleRows
  const showKinds = template.format === 'xlsx'

  return (
    <Card>
      <CardHeading
        icon="table"
        title="Preview"
        subtitle={
          template.shiftCount === 0 ? 'Scroll sideways to see every column.' : 'Scroll sideways to see the shift columns.'
        }
      />

      {showKinds && (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-4 tw:gap-y-2 tw:border-b tw:border-gray-100 tw:px-5 tw:py-3 tw:text-xs tw:text-gray-cool-600">
          <span className="tw:font-medium tw:text-gray-cool-900">Cell types:</span>
          {KIND_LEGEND.map(({ kind, color, note }) => (
            <span key={kind} className="tw:flex tw:items-center tw:gap-1.5">
              <Badge color={color} shape="rounded">
                {KIND_LABELS[kind]}
              </Badge>
              {note}
            </span>
          ))}
        </div>
      )}

      <div className="tw:overflow-x-auto">
        <table className="tw:min-w-full tw:border-collapse tw:text-xs">
          <thead>
            <tr className="tw:bg-gray-cool-50">
              <th className="tw:sticky tw:left-0 tw:z-10 tw:w-10 tw:border-b tw:border-r tw:border-gray-200 tw:bg-gray-cool-50 tw:px-2 tw:py-2 tw:text-gray-cool-400">
                <span className="tw:sr-only">Row</span>
              </th>
              {columns.map((column) => (
                <th
                  key={column.name}
                  scope="col"
                  className="tw:whitespace-nowrap tw:border-b tw:border-gray-200 tw:px-3 tw:py-2 tw:text-left tw:align-bottom"
                >
                  <span
                    className={clsx(
                      'tw:block tw:font-mono tw:font-medium',
                      column.name.startsWith('shift_') ? 'tw:text-primary-700' : 'tw:text-gray-cool-900'
                    )}
                  >
                    {column.name}
                  </span>
                  {showKinds && (
                    <span className="tw:mt-1 tw:block tw:text-[0.625rem] tw:font-normal tw:uppercase tw:tracking-wide tw:text-gray-cool-400">
                      {KIND_LABELS[column.kind]}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowCount === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="tw:px-3 tw:py-6 tw:text-center tw:text-sm tw:text-gray-cool-600"
                >
                  Headers only. This template has no example rows.
                </td>
              </tr>
            ) : (
              Array.from({ length: rowCount }).map((_, rowIndex) => (
                <tr key={rowIndex} className="tw:odd:bg-white tw:even:bg-gray-cool-50/40">
                  <td className="tw:sticky tw:left-0 tw:z-10 tw:border-r tw:border-gray-200 tw:bg-white tw:px-2 tw:py-2 tw:text-center tw:text-gray-cool-400">
                    {rowIndex + 2}
                  </td>
                  {columns.map((column) => (
                    <td
                      key={column.name}
                      className="tw:whitespace-nowrap tw:px-3 tw:py-2 tw:text-gray-cool-700"
                      title={sampleFor(column.name, rowIndex)}
                    >
                      <span className="tw:flex tw:max-w-56 tw:items-center tw:gap-1 tw:truncate">
                        {sampleFor(column.name, rowIndex)}
                        {showKinds && column.kind === 'list' && sampleFor(column.name, rowIndex) && (
                          <Icon type="chevron-down" className="tw:h-3 tw:w-3 tw:shrink-0 tw:text-gray-cool-400" />
                        )}
                      </span>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Step 2 — upload
// ---------------------------------------------------------------------------

type CheckPhase = {
  title: string
  subtitle: string
  icon: IconTypes
  items: string[]
}

const checkPhases = (shiftCount: number): CheckPhase[] => [
  {
    title: 'Before you submit',
    subtitle: 'Problem cells are highlighted. Fix them on screen.',
    icon: 'check-done-01',
    items: [
      'Your column names are matched to the template, even if you renamed or moved them.',
      'Group, event type, status and time zone must be one of the listed values.',
      'Dates must be YYYY-MM-DD. Times must look like 9:00AM.',
      'Required cells cannot be empty. Volunteer counts must be whole numbers.',
      `Files with more than ${MAX_EVENT_ROWS} events are stopped here.`,
    ],
  },
  {
    title: 'After you submit',
    subtitle:
      shiftCount === 0
        ? 'SupporterBase creates the events in the background.'
        : 'SupporterBase creates the events and rosters in the background.',
    icon: 'refresh',
    items:
      shiftCount === 0
        ? ['Each row becomes one event, with an empty roster.', 'Events appear within a few minutes.']
        : [
            'Each row becomes one event. Its shifts go on that event roster.',
            'Volunteer counts become empty shift slots.',
            'Events appear within a few minutes.',
          ],
  },
]

function UploadStep({ template, columns }: { template: TemplateState; columns: ColumnSpec[] }) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-5">
      <Card>
        <CardHeading
          icon="upload-cloud-02"
          title="Upload your file"
          subtitle="Excel or CSV. Fix any problems on screen before you submit."
        />
        <div className="tw:p-5">
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-xl tw:border-2 tw:border-dashed tw:border-gray-cool-200 tw:bg-gray-cool-50/50 tw:px-6 tw:py-10 tw:text-center">
            <span className="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:text-primary-600 tw:shadow-xs">
              <Icon type="upload-cloud-02" className="tw:h-6 tw:w-6" />
            </span>
            <p className="tw:text-sm tw:font-medium tw:text-gray-cool-900">Drop your file here, or choose one</p>
            <p className="tw:max-w-md tw:text-xs tw:text-gray-cool-600">
              Checked against your {columns.length} columns
              {template.shiftCount === 0 ? '. This template has no shift columns.' : `, with up to ${template.shiftCount} shifts per event.`}
            </p>
            <Button size="xl" leadingIcon="plus-circle">
              Choose file
            </Button>
            <button type="button" className="tw:text-xs tw:font-medium tw:text-primary-700 tw:hover:text-primary-900">
              Download the template again
            </button>
          </div>
        </div>
      </Card>

      {checkPhases(template.shiftCount).map((phase) => (
        <Card key={phase.title}>
          <CardHeading icon={phase.icon} title={phase.title} subtitle={phase.subtitle} />
          <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5 tw:text-sm">
            {phase.items.map((line) => (
              <li key={line} className="tw:flex tw:items-start tw:gap-2.5 tw:py-3 tw:text-gray-cool-700">
                <Icon type="check-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" />
                {line}
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3 — confirm
// ---------------------------------------------------------------------------

const SUBMITTED_GROUPS = [
  { name: 'Warringah Volunteers', rows: 21, href: '/events?group=warringah-volunteers' },
  { name: 'Mackellar Volunteers', rows: 13, href: '/events?group=mackellar-volunteers' },
]

function ConfirmStep() {
  return (
    <div className="tw:flex tw:flex-col tw:gap-5">
      <Card>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3 tw:border-b tw:border-gray-200 tw:px-5 tw:py-4">
          <span className="tw:flex tw:h-9 tw:w-9 tw:items-center tw:justify-center tw:rounded-full tw:bg-success-50 tw:text-success-600">
            <Icon type="check-circle" className="tw:h-5 tw:w-5" />
          </span>
          <div>
            <h3 className="tw:text-base tw:font-bold tw:text-gray-cool-900">File submitted</h3>
            <p className="tw:text-sm tw:text-gray-cool-600">week-1-doorknocks.xlsx · 2 Sep 2026, 9:14am</p>
          </div>
        </div>

        <div className="tw:grid tw:divide-y tw:divide-gray-100 tw:sm:grid-cols-3 tw:sm:divide-x tw:sm:divide-y-0">
          {[
            { value: '34', label: 'Rows accepted' },
            { value: '0', label: 'Rows you skipped' },
            { value: '2', label: 'Groups in this file' },
          ].map((stat) => (
            <div key={stat.label} className="tw:px-5 tw:py-4">
              <p className="tw:text-2xl tw:font-bold tw:text-gray-cool-900">{stat.value}</p>
              <p className="tw:text-xs tw:text-gray-cool-600">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="tw:border-t tw:border-gray-100 tw:px-5 tw:py-4">
          <Alert type="info" size="sm">
            <AlertText>
              Events and rosters are being created. Check the group's events in a few minutes.
            </AlertText>
          </Alert>
        </div>
      </Card>

      <Card>
        <CardHeading icon="calendar-check-01" title="View the events" />
        <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100">
          {SUBMITTED_GROUPS.map((group) => (
            <li key={group.name} className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:px-5 tw:py-3">
              <span className="tw:flex tw:flex-col tw:gap-0.5">
                <span className="tw:text-sm tw:font-medium tw:text-gray-cool-900">{group.name}</span>
                <span className="tw:text-xs tw:text-gray-cool-600">{group.rows} rows</span>
              </span>
              <Link href={group.href} rank="secondary" size="sm" trailingIcon="external-link">
                View events
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const ROOT_ID = 'event-imports-preview'

/** Step names for the `?step=` param, in the order they appear. */
const STEP_SLUGS = ['template', 'upload', 'confirm'] as const

/** Reads `?step=`, accepting either the slug or the 1-based number. */
function initialStep(): number {
  try {
    const raw = new URLSearchParams(window.location.search).get('step')
    if (!raw) return 0

    const bySlug = STEP_SLUGS.indexOf(raw.toLowerCase() as (typeof STEP_SLUGS)[number])
    if (bySlug !== -1) return bySlug

    const byNumber = Number.parseInt(raw, 10) - 1
    return byNumber >= 0 && byNumber < STEP_SLUGS.length ? byNumber : 0
  } catch {
    return 0
  }
}

const EventImportsPreview = () => {
  const [step, setStep] = useState(initialStep)
  const [template, setTemplate] = useState<TemplateState>(INITIAL_TEMPLATE)

  const [hasDownloaded, setHasDownloaded] = useState(false)

  const [showDesign, setShowDesign] = useState(false)
  const [layout, setLayout] = useState<'split' | 'stacked'>('split')

  const columns = useMemo(() => buildColumns(template), [template])
  const fullFileName = `${template.fileName || 'events-import'}.${template.format}`

  // Each step is a new screen, so it starts at the top. PageLayout.Root is the
  // scrolling element here, not the window.
  const goToStep = (next: number) => {
    setStep(next)
    document.getElementById(ROOT_ID)?.scrollTo({ top: 0 })
    window.scrollTo({ top: 0 })

    try {
      const url = new URL(window.location.href)
      url.searchParams.set('step', STEP_SLUGS[next])
      window.history.replaceState({}, '', url)
    } catch {
      // no-op outside the browser
    }
  }

  // Changing the template invalidates whatever they already downloaded.
  const updateTemplate: React.Dispatch<React.SetStateAction<TemplateState>> = (action) => {
    setHasDownloaded(false)
    setTemplate(action)
  }

  return (
    <PageLayout.Root id={ROOT_ID}>
      <PageLayout.Header heading="Import events and rosters" />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-col tw:gap-6">
          <div className="tw:py-2">
            <Steps steps={STEP_LABELS} currentStep={step} />
          </div>

          <div className="tw:flex tw:justify-center tw:gap-2">
            {STEP_LABELS.map((label, index) => (
              <button
                key={label}
                type="button"
                aria-current={index === step}
                onClick={() => goToStep(index)}
                className={clsx(
                  'tw:rounded-full tw:px-3 tw:py-1 tw:text-xs tw:font-medium tw:transition',
                  index === step
                    ? 'tw:bg-primary-600 tw:text-white'
                    : 'tw:bg-gray-cool-100 tw:text-gray-cool-600 tw:hover:bg-gray-cool-200'
                )}
              >
                {index + 1}. {label}
              </button>
            ))}
          </div>

          {step === 0 && (
            <>
              {layout === 'split' ? (
                <div className="tw:grid tw:gap-6 tw:lg:grid-cols-[minmax(0,1fr)_20rem]">
                  <TemplateBuilder template={template} setTemplate={updateTemplate} />
                  <TemplateSummary template={template} columns={columns} setTemplate={updateTemplate} />
                </div>
              ) : (
                <div className="tw:flex tw:flex-col tw:gap-6">
                  <TemplateBuilder template={template} setTemplate={updateTemplate} />
                  <TemplateSummary template={template} columns={columns} setTemplate={updateTemplate} />
                </div>
              )}

              <FilePreview template={template} columns={columns} />
            </>
          )}

          {step === 1 && (
            <>
              <UploadStep template={template} columns={columns} />
            </>
          )}

          {step === 2 && (
            <>
              <ConfirmStep />
            </>
          )}
        </div>
      </PageLayout.Body>

      <PageLayout.Footer>
        <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-wrap tw:items-center tw:gap-3">
          {step === 0 && (
            <>
              <span className="tw:mr-auto tw:flex tw:min-w-0 tw:items-center tw:gap-2 tw:text-sm">
                {hasDownloaded ? (
                  <>
                    <Icon type="check-circle" className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" />
                    <span className="tw:truncate tw:text-gray-cool-600">
                      Downloaded <span className="tw:font-medium tw:text-gray-cool-900">{fullFileName}</span>
                    </span>
                  </>
                ) : (
                  <span className="tw:truncate tw:text-gray-cool-600">
                    {columns.length} columns ·{' '}
                    {template.shiftCount === 0 ? 'no shift columns' : `${template.shiftCount} shifts per event`}
                  </span>
                )}
              </span>

              <div className="tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                <Button
                  size="xl"
                  rank={hasDownloaded ? 'secondary' : 'primary'}
                  leadingIcon="file-download-03"
                  disabled={template.groups.length === 0}
                  onClick={() => setHasDownloaded(true)}
                >
                  {hasDownloaded
                    ? 'Download again'
                    : `Download ${template.format === 'xlsx' ? 'Excel' : 'CSV'} template`}
                </Button>

                <Button size="xl" rank={hasDownloaded ? 'primary' : 'secondary'} onClick={() => goToStep(1)}>
                  {hasDownloaded ? 'Next: upload your file' : 'I already have a file'}
                </Button>
              </div>
            </>
          )}

          {step === 1 && (
            <div className="tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-3">
              <Button rank="secondary" onClick={() => goToStep(0)}>
                Back to the template
              </Button>
              <Button size="xl" onClick={() => goToStep(2)}>
                Next: confirm
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="tw:ml-auto tw:flex tw:flex-wrap tw:items-center tw:gap-3">
              <Button rank="secondary" leadingIcon="plus-circle" onClick={() => goToStep(0)}>
                Import another file
              </Button>
            </div>
          )}
        </div>
      </PageLayout.Footer>

      <div>
        {/* Design tools: dev-only floating overlay, portaled to body, default closed */}
        {createPortal(
          <>
            <button
              onClick={() => setShowDesign((s) => !s)}
              className="tw:fixed tw:bottom-4 tw:left-4 tw:z-50 tw:flex tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-gray-300 tw:bg-white tw:px-3.5 tw:py-2 tw:text-sm tw:font-medium tw:text-gray-600 tw:shadow-md tw:hover:border-primary-400 tw:hover:text-primary-600"
            >
              <Icon type="eye" className="tw:h-4 tw:w-4" />
              {showDesign ? 'Hide design tools' : 'Design tools'}
            </button>
            {showDesign && (
              <div className="tw:fixed tw:bottom-16 tw:left-4 tw:z-50 tw:w-[min(720px,calc(100vw-2rem))] tw:rounded-xl tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:p-5 tw:shadow-xl">
                <div className="tw:mb-2 tw:flex tw:items-center tw:gap-2">
                  <Icon type="eye" className="tw:h-4 tw:w-4 tw:text-gray-400" />
                  <span className="tw:text-sm tw:font-bold tw:text-gray-700">Design exploration</span>
                  <button
                    onClick={() => setShowDesign(false)}
                    aria-label="Hide design tools"
                    className="tw:ml-auto tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:text-gray-500 tw:hover:border-primary-400 tw:hover:text-primary-600"
                  >
                    <Icon type="x-close" className="tw:h-4 tw:w-4" />
                  </button>
                </div>
                <p className="tw:mb-4 tw:text-xs tw:leading-relaxed tw:text-gray-500">
                  This panel is purely for exploring the design and is not part of the product.
                  <br />
                  Questo pannello serve solo a esplorare il design e non fa parte del prodotto.
                </p>
                <div className="tw:flex tw:flex-wrap tw:gap-8">
                  <SegControl
                    label="Builder layout"
                    value={layout}
                    onChange={(v) => setLayout(v as 'split' | 'stacked')}
                    options={[
                      { value: 'split', label: 'Sticky summary' },
                      { value: 'stacked', label: 'Stacked' },
                    ]}
                  />
                  <SegControl
                    label="File format"
                    value={template.format}
                    onChange={(v) => setTemplate((p) => ({ ...p, format: v as FileFormat }))}
                    options={[
                      { value: 'xlsx', label: 'Excel' },
                      { value: 'csv', label: 'CSV' },
                    ]}
                  />
                  <SegControl
                    label="Step"
                    value={String(step)}
                    onChange={(v) => goToStep(Number(v))}
                    options={[
                      { value: '0', label: 'Template' },
                      { value: '1', label: 'Upload' },
                      { value: '2', label: 'Confirm' },
                    ]}
                  />
                </div>
              </div>
            )}
          </>,
          document.body
        )}
      </div>
    </PageLayout.Root>
  )
}

export default EventImportsPreview
